import { makeStyles } from "@material-ui/core";
import {
  Box,
  Button,
  Grid,
  Drawer,
  Typography,
  Switch,
  Table,
  Fab,
  TableContainer,
  TablePagination,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  TextField,
  Tooltip,
  InputAdornment,
} from "@material-ui/core";
import { CheckCircle } from "@material-ui/icons";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useContext, useEffect, useReducer, useState } from "react";
import { toast } from "react-toastify";
import { KTSVG } from "../../../_metronic/helpers";
import CampaignModal from "../../components/CampaignModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TestMessageCampaignModal from "../../components/TestMessageCampaignModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import socket from "../../hooks/useSocket";
import JsonParse from "../../helpers/JsonParse";
import SearchIcon from "@material-ui/icons/Search";
import QueueSelectConnection from "../../components/Queue/queueSelectConnection";
import MultiSelect from "../../components/InputStyled/multiselect";
import FormatDate from "../../helpers/FormatDate";
import FilterListOutlinedIcon from "@material-ui/icons/FilterListOutlined";

const useStyles = makeStyles((theme) => ({
  modalStyle: {
    padding: "20px",
    maxheight: "95vh!important",
    minWidth: "670px",
    background: "#ffffff",
    marginTop: "10px",
    marginLeft: "10px",
    marginRight: "10px",
    borderRadius: "10px",
    boxShadow:
      "0 2px 2px 0 rgba(0,0,0,0.14),0 3px 1px -2px rgba(0,0,0,0.12),0 1px 5px 0 rgba(0,0,0,0.2)",
    "@media (max-width: 740px)": {
      padding: "0px !important",
      marginTop: "65px",
    },
  },
  container: {
    display: "flex",
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  inputDate: {
    height: "38px",
    lineHeight: "38px",
    width: "100%",
    padding: "0 10px",
    borderRadius: "3px",
    border: "1px solid rgb(196, 196, 196)",
  },
  connectionFilter: {
    height: "38px",
    lineHeight: "38px",
    padding: "0 0 0 10px",
    "& span:nth-of-type(1)": {
      justifyContent: "flex-start",
      "& span:nth-of-type(1)": {
        position: "absolute",
        marginLeft: "0",
        marginRight: "0",
        right: "5px",
      },
    },
  },
  filterColumn: {
    [theme.breakpoints.down(1280)]: {
      display: "none",
    },
  },
  filterMobile: {
    width: 300,
    padding: 10,
  },
  filterMobileButton: {
    bottom: "10px",
    position: "absolute",
    right: "10px",
    display: "none",
    [theme.breakpoints.down(1280)]: {
      display: "block",
    },
  },
}));

const reducer = (state = [], action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    return !campaigns ? [] : campaigns;
  }

  if (action.type === "UPDATE_CAMPAIGN") {
    let { campaign } = action.payload;
    if (!campaign) campaign = action.payload;
    if (!campaign) return;

    const campaignIndex = state.findIndex((c) => c.id === campaign.id);

    if (campaignIndex !== -1) {
      state[campaignIndex] = campaign;
      return [...state];
    } else {
      return [campaign, ...state];
    }
  }

  if (action.type === "DELETE_CAMPAIGN") {
    const campaignId = action.payload;

    const campaignIndex = state.findIndex((c) => c.id === campaignId);
    if (campaignIndex !== -1) {
      state.splice(campaignIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) =>
        descendingComparator(
          String(a).toLowerCase(),
          String(b).toLowerCase(),
          orderBy
        )
    : (a, b) =>
        -descendingComparator(
          String(a).toLowerCase(),
          String(b).toLowerCase(),
          orderBy
        );
};

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

const headCells = [
  {
    id: "name",
    numeric: false,
    disablePadding: true,
    label: "Nome",
    sortable: true,
  },
  {
    id: "begin",
    numeric: false,
    disablePadding: false,
    label: "Início",
    sortable: true,
  },
  {
    id: "end",
    numeric: false,
    disablePadding: false,
    label: "Término",
    sortable: true,
  },
  {
    id: "createdAt",
    numeric: false,
    disablePadding: false,
    label: "Data de Criação",
    sortable: true,
  },
  {
    id: "days",
    numeric: false,
    disablePadding: false,
    label: "Dias da Semana",
    sortable: false,
  },
  {
    id: "status",
    numeric: false,
    disablePadding: false,
    label: "Status",
    sortable: true,
  },
  {
    id: "connection",
    numeric: false,
    disablePadding: false,
    label: "Conexão",
    sortable: false,
  },
  {
    id: "message",
    numeric: false,
    disablePadding: false,
    label: "Mensagem",
    sortable: false,
  },
  {
    id: "actions",
    numeric: true,
    disablePadding: false,
    label: "Ações",
    sortable: false,
  },
];

const EnhancedTableHead = (props) => {
  const { classes, order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <>
      <TableHead>
        <TableRow>
          {headCells.map((headCell) => {
            const isSortable = headCell.sortable !== false; // Verifica se a coluna é ordenável
            return (
              <TableCell
                key={headCell.id}
                align={headCell.numeric ? "right" : "left"}
                padding={headCell.disablePadding ? "none" : "normal"}
                sortDirection={
                  isSortable && orderBy === headCell.id ? order : false
                }
              >
                {isSortable ? (
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : "asc"}
                    onClick={createSortHandler(headCell.id)}
                  >
                    {headCell.label}
                  </TableSortLabel>
                ) : (
                  headCell.label // Sem TableSortLabel para colunas não ordenáveis
                )}
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    </>
  );
};

const Campaign = () => {
  const classes = useStyles();

  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [campaigns, dispatch] = useReducer(reducer, []);
  const [whatsapp, setWhatsapp] = useState();
  const [loading, setLoading] = useState(false);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [testCampaignModalOpen, setTestCampaignModalOpen] = useState(false);
  const [resendCampaignModalOpen, setResendCampaignModalOpen] = useState(false);
  const [duplicateCampaignModalOpen, setDuplicateCampaignModalOpen] =
    useState(false);
  const [users, setUsers] = useState([]);

  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("begin");
  const [page, setPage] = useState(1);
  const [totals, setTotals] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchForParam, setSearchForParam] = useState(false);
  const [searchParam, setSearchParam] = useState("");  
  const thirtyDaysAgo = new Date(
    Date.now() - 30 * 24 * 60 * 60 * 1000
  ).toISOString();
  const twelveMonthsLater = new Date(
    Date.now() + 365 * 24 * 60 * 60 * 1000
  ).toISOString();

  const filtersInitialState = {
    connectionAndQueueIds: user?.connectionsAndQueues || [],
    userIds: [],
    originDate: "begin",
    begin: FormatDate(thirtyDaysAgo, false, 2),
    end: FormatDate(twelveMonthsLater, false, 2),
  };
  const [filters, setFilters] = useState(filtersInitialState);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/whatsapp/", {
          signal: abortController.signal,
        });
        if (!isMounted) return;
        setWhatsapp(JSON.stringify(data));

        const { data: usrData } = await api.get("/usersList/", {
          signal: abortController.signal,
        });
        if (!isMounted) return;
        if (usrData) setUsers(usrData);
        setTimeout(() => {
          setLoading(false);
          searchForParam(!searchForParam);
        }, 1000);
      } catch (err) {
        setLoading(false);
        if (!isMounted || err.name == "AbortError") return;
        console.error({ err });
        toastError(err);
      }
    })();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    const campaignEventName = `campaign-${user.adminId}`;
    const campaignEventFunction = async (data) => {
      if (data.action === "update" || data.action === "create") {
        const { campaign } = data;
        // verificar filtro, se passar atualizar, se não deletar
        if (filterCampaign(campaign) == true)
          dispatch({ type: "UPDATE_CAMPAIGN", payload: campaign });
        else dispatch({ type: "DELETE_CAMPAIGN", payload: campaign.id });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CAMPAIGN", payload: +data.campaignId });
      }
    };
    socket.on(campaignEventName, campaignEventFunction);
    return () => {
      socket.off(campaignEventName, campaignEventFunction);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.post(
          "/campaignSearch/",
          {
            page,
            order,
            orderBy,
            searchParam,
            ...filters,
          },
          {
            signal: abortController.signal,
          }
        );

        if (!isMounted) return;
        if (data?.campaigns)
          dispatch({ type: "LOAD_CAMPAIGNS", payload: data.campaigns });
        if (data.count) setTotals(data.count);
      } catch (err) {
        if (!isMounted || err.name == "AbortError") return;
        console.error({ err });
        toastError(err);
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 500);
      }
    })();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [page, rowsPerPage, order, orderBy, filters, searchForParam]);

  const handleSearch = (str) => {
    setSearchParam(str.toLowerCase());
  };

  const filterCampaign = (campaign) => {
    if (!campaign) return false;

    // Verificar consulta por texto
    if (searchParam && searchParam.trim() !== "") {
      const searchLower = searchParam.toLowerCase();
      if (
        !campaign.name.toLowerCase().includes(searchLower) &&
        !(
          campaign.message &&
          campaign.message.toLowerCase().includes(searchLower)
        )
      ) {
        return false;
      }
    }

    // Verificar usuários selecionados
    if (filters.userIds && filters.userIds.length > 0) {
      if (!filters.userIds.includes(campaign.userId)) return false;
    }

    // Verificar conexões e filas selecionadas
    if (
      filters.connectionAndQueueIds &&
      filters.connectionAndQueueIds.length > 0
    ) {
      const connFound = filters.connectionAndQueueIds.find(
        (scc) =>
          scc.connectionId == campaign.whatsappId &&
          (!scc.queueIds ||
            scc.queueIds.length === 0 ||
            scc.queueIds.includes(campaign.queue))
      );
      if (!connFound) return false;
    }

    // Verificar datas - abordagem mais robusta
    try {
      // Converter strings de data para objetos Date
      const parseDate = (dateString) => {
        if (!dateString) return null;
        const parts = dateString.split("-");
        if (parts.length === 3) {
          return new Date(parts[0], parts[1] - 1, parts[2]);
        }
        return new Date(dateString);
      };

      const beginDate = parseDate(filters.begin);
      const endDate = parseDate(filters.end);
      const campaignDate = parseDate(campaign[filters.originDate]);

      // Se alguma data for inválida, não aplicar filtro
      if (!beginDate || !endDate || !campaignDate) return true;
      if (
        isNaN(beginDate.getTime()) ||
        isNaN(endDate.getTime()) ||
        isNaN(campaignDate.getTime())
      )
        return true;

      // Ajustar endDate para incluir todo o dia
      const adjustedEndDate = new Date(endDate);
      adjustedEndDate.setHours(23, 59, 59, 999);

      return campaignDate >= beginDate && campaignDate <= adjustedEndDate;
    } catch (err) {
      console.error("Erro ao filtrar datas:", err);
      return true;
    }
  };

  const hadleEditCampaign = (campaingId) => {
    setSelectedCampaign(campaingId);
    setCampaignModalOpen(true);
  };

  const hadleDuplicateCampaign = (campanha) => {
    setCampaignModalOpen(true);
    setSelectedCampaign(campanha);
    setDuplicateCampaignModalOpen(true);
  };

  const handleCloseCampaignModal = async () => {
    setCampaignModalOpen(false);
    setSelectedCampaign(null);
    setResendCampaignModalOpen(false);
    setDuplicateCampaignModalOpen(false);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedCampaign(null);
  };

  const handleCloseTestCampaignModal = () => {
    setTestCampaignModalOpen(false);
    setSelectedCampaign(null);
  };

  const handleDeleteCampaign = async (id) => {
    setLoading(true);
    try {
      await api.delete(`/campaign/${id}`);
      setTimeout(async () => {
        toast.success(i18n.t("Campanha deletada com sucesso!"));
        setLoading(false);
      }, 1000);
    } catch (err) {
      console.error({ err });
      toastError(err);
      setLoading(false);
    }
    setSelectedCampaign(null);
  };

  const handleStatusCampaign = async (id, status) => {
    setLoading(true);
    try {
      var value = { status };
      await api.put(`/campaign/status/${id}`, value);
      if (value.status == "paused") {
        toast.success(i18n.t(`A campanha foi pausada.`));
      }
      if (value.status == "started") {
        toast.success(i18n.t(`A campanha foi reiniciada!`));
      }
    } catch (err) {
      console.error({ err });
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const addHours = (numOfHours, date = new Date()) => {
    date.setTime(date.getTime() + numOfHours * 60 * 60 * 1000);
    return date.toISOString().replace(/\.\d{3}Z$/, "");
  };

  const renderArchiveInfo = () => {
    return (
      <>
        <div
          title="Esta campanha tem um arquivo"
          style={{ display: "flex", marginTop: "10px" }}
        >
          Arquivo
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            class="bi bi-file-earmark-zip"
            viewBox="0 0 16 16"
          >
            <path d="M5 7.5a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v.938l.4 1.599a1 1 0 0 1-.416 1.074l-.93.62a1 1 0 0 1-1.11 0l-.929-.62a1 1 0 0 1-.415-1.074L5 8.438V7.5zm2 0H6v.938a1 1 0 0 1-.03.243l-.4 1.598.93.62.929-.62-.4-1.598A1 1 0 0 1 7 8.438V7.5z" />
            <path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1h-2v1h-1v1h1v1h-1v1h1v1H6V5H5V4h1V3H5V2h1V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5h-2z" />
          </svg>
        </div>
      </>
    );
  };

  const goToReport = (data) => {
    const url = `/relatory/${data}`;
    window.open(url, "_blank");
  };

  const handleSendMessageCampaign = async (
    campaignId,
    number,
    messageKeys,
    file
  ) => {
    const fetchSession = async () => {
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append("campaignId", campaignId);
        formData.append("number", number);
        formData.append("messageKeys", JSON.stringify(messageKeys));
        if (file) formData.append("medias", file);

        await api.post("/campaignTest", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        handleCloseTestCampaignModal();
      } catch (err) {
        console.error({ err });
        toastError(err);
      } finally {
        setLoading(false);
        handleCloseTestCampaignModal();
      }
    };
    fetchSession();
  };

  const handleOpenQueueModal = () => {
    setCampaignModalOpen(true);
    setSelectedCampaign(null);
  };

  const RenderWeekDays = (props) => {
    const { days } = props;
    const daysOk = JsonParse(days);
    return daysOk.map((dias) => (
      <div
        style={{
          backgroundColor: "#0B708C",
          margin: "1px",
          height: "25px",
          borderRadius: "25px",
          width: "25px",
          display: "inline-block",
          position: "relative",
        }}
      >
        <p
          style={{
            width: "25px",
            height: "25px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "#ffffff",
            fontFamily: "initial",
            fontSize: "10px",
          }}
        >
          {dias}
        </p>
      </div>
    ));
  };

  const handleChangePage = (event, newPage) => {
    // Verifica se a nova página está dentro dos limites
    if (newPage < 1) {
      setPage(1);
    } else if (newPage >= totals) {
      setPage(totals - 1);
    } else {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const RenderFilters = () => {
    return (
      <>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography style={{ marginBottom: "10px" }}>
              Pesquisa por Texto
            </Typography>
            <TextField
              placeholder="Pesquisar por nome"
              variant="outlined"
              size="small"
              type="search"
              value={filtersInitialState?.searchParam}
              style={{ width: "100%" }}
              onChange={(e) => {
                handleSearch(e.target.value);
              }}
              onKeyUp={(e) => {
                if (e.target.value == "") {
                  setSearchForParam(!searchForParam);
                  return;
                }
                const key = e.charCode || e.keyCode || 0;
                if (key === 13) {
                  e.preventDefault();
                  setSearchForParam(!searchForParam);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    onClick={(e) => {
                      setSearchForParam(!searchForParam);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <Tooltip
                      title={
                        <Typography gutterBottom color="inherit">
                          {i18n.t("contacts.filters.searchTooltip")}
                        </Typography>
                      }
                      placement="bottom"
                    >
                      <SearchIcon style={{ color: "gray" }} />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography style={{ marginBottom: "5px" }}>Conexão</Typography>
            <QueueSelectConnection
              selecteds={filters.connectionAndQueueIds}
              onSelected={(opt) => {
                setFilters((prev) => {
                  return { ...prev, connectionAndQueueIds: opt };
                });
              }}
              isCollapsed={true}
              userId={user?.id || undefined}
              className={classes.connectionFilter}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MultiSelect
              title="Usuário"
              displayText={"selecionar usuário"}
              name="campaignFilterUser"
              key="campaignFilterUser"
              options={users.map((usr) => {
                return { key: usr.id, value: usr.name, disabled: false };
              })}
              onSelected={(opt) => {
                setFilters((prev) => {
                  return { ...prev, userIds: opt };
                });
              }}
              selected={filters.userIds}
              closeOnSelect={true}
              disabled={false}
              style={{
                lineHeight: "38px",
                height: "38px",
                marginBottom: "0",
                paddingRight: "10px",
              }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            Data de Início
            <Switch
              id="campaignFilterOriginDate"
              name="campaignFilterOriginDate"
              color="primary"
              checked={filters.originDate == "begin" ? true : false}
              onChange={() => {
                setFilters((prev) => {
                  return {
                    ...prev,
                    originDate: prev.originDate == "begin" ? "end" : "begin",
                  };
                });
              }}
            />
            Data de Finalização
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography style={{ marginBottom: "5px" }}>
              Data de Início
            </Typography>
            <input
              id="campaignFilterBegin"
              name="campaignFilterBegin"
              value={filters.begin || ""}
              type="date"
              className={classes.inputDate}
              onChange={(e) => {
                const newDate = e.target.value;
                if (filters.end && newDate > filters.end) {
                  toast.error(
                    "A data de início não pode ser posterior à data de término"
                  );
                  return;
                }
                setFilters((prev) => ({ ...prev, begin: newDate }));
              }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography style={{ marginBottom: "5px" }}>
              Data de Término
            </Typography>
            <input
              id="campaignFilterEnd"
              name="campaignFilterEnd"
              value={filters.end || ""}
              type="date"
              className={classes.inputDate}
              onChange={(e) => {
                const newDate = e.target.value;
                if (filters.begin && newDate < filters.begin) {
                  toast.error(
                    "A data de término não pode ser anterior à data de início"
                  );
                  return;
                }
                setFilters((prev) => ({ ...prev, end: newDate }));
              }}
            />
          </Grid>
        </Grid>
      </>
    );
  };

  const handleDrawer = () => {
    setOpen(!open);
  };

  return (
    <>
      <ConfirmationModal
        title={
          selectedCampaign && `Excluir Campanha: ${selectedCampaign.name}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteCampaign(selectedCampaign.id)}
      >
        Deseja realmente excluir a campanha?
      </ConfirmationModal>
      <TestMessageCampaignModal
        title={
          selectedCampaign &&
          `Enviando teste da campanha ${selectedCampaign.name}`
        }
        loading={loading}
        open={testCampaignModalOpen}
        onClose={handleCloseTestCampaignModal}
        campaignId={selectedCampaign?.id}
        onConfirm={(campaignId, number, messageKeys, file) =>
          handleSendMessageCampaign(campaignId, number, messageKeys, file)
        }
      ></TestMessageCampaignModal>
      <CampaignModal
        open={campaignModalOpen}
        onClose={handleCloseCampaignModal}
        campaignId={selectedCampaign}
        duplicateCampaign={duplicateCampaignModalOpen}
        whatsappQuery={whatsapp}
        resendCampaign={resendCampaignModalOpen}
      />
      <Box component="div" className={classes.modalStyle}>
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            style={{ marginBottom: "20px", position: "relative" }}
          >
            <Typography variant="h5" style={{ fontWeight: "600" }}>
              Campanhas
            </Typography>
            <Button
              style={{
                position: "absolute",
                right: "10px",
                top: "10px",
              }}
              variant="contained"
              color="primary"
              onClick={handleOpenQueueModal}
            >
              <KTSVG
                path="media/icons/duotune/arrows/arr075.svg"
                className="svg-icon-3"
                style={{ marginRight: "10px" }}
              />
              Adicionar Campanha
            </Button>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12} lg={10} xl={10} mb={2}>
            <TableContainer
              style={{
                height: "calc(100vh - 220px)",
              }}
            >
              <Table
                aria-labelledby="tableTitle"
                size={"small"}
                aria-label="enhanced table"
                stickyHeader
              >
                <EnhancedTableHead
                  classes={classes}
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  rowCount={campaigns?.length || 0}
                />
                {loading ? (
                  <TableBody>
                    <TableRowSkeleton columns={headCells.length} />
                  </TableBody>
                ) : (
                  <TableBody>
                    {!campaigns || campaigns?.length == 0 ? (
                      <TableRow>
                        <TableCell colSpan={headCells.length} align="center">
                          Nenhum contato encontrado.
                        </TableCell>
                      </TableRow>
                    ) : (
                      stableSort(campaigns, getComparator(order, orderBy)).map(
                        (row, index) => {
                          return (
                            <TableRow hover tabIndex={-1} key={row.id}>
                              <TableCell align="left">{row.name}</TableCell>
                              <TableCell align="left">
                                {format(
                                  parseISO(
                                    addHours(-3, new Date(row.begin))
                                      .toLocaleString("pt-BR", {
                                        timeZone: "America/Sao_Paulo",
                                      })
                                      .slice(0, 16)
                                      .replace("T", " ")
                                  ),
                                  "dd/MM/yyyy HH:mm",
                                  { locale: ptBR }
                                )}
                              </TableCell>
                              <TableCell align="left">
                                {format(
                                  parseISO(
                                    addHours(-3, new Date(row.end))
                                      .toLocaleString("pt-BR", {
                                        timeZone: "America/Sao_Paulo",
                                      })
                                      .slice(0, 16)
                                      .replace("T", " ")
                                  ),
                                  "dd/MM/yyyy HH:mm",
                                  { locale: ptBR }
                                )}
                              </TableCell>
                              <TableCell align="left">
                                {format(
                                  parseISO(
                                    addHours(-3, new Date(row.createdAt))
                                      .toLocaleString("pt-BR", {
                                        timeZone: "America/Sao_Paulo",
                                      })
                                      .slice(0, 16)
                                      .replace("T", " ")
                                  ),
                                  "dd/MM/yyyy HH:mm",
                                  { locale: ptBR }
                                )}
                              </TableCell>
                              <TableCell
                                align="left"
                                style={{
                                  width: 200,
                                  paddingLeft: 0,
                                  paddingRight: 0,
                                }}
                              >
                                <RenderWeekDays days={row.days} />
                              </TableCell>
                              <TableCell align="left">
                                {i18n.t(`campaign.status.${row.status}`)}
                              </TableCell>
                              <TableCell align="left">
                                {row.connection}
                              </TableCell>
                              <TableCell align="left">
                                {row.message == "undefinedForUser" ? (
                                  renderArchiveInfo()
                                ) : row.message && row.media ? (
                                  <>
                                    {row.message} + {renderArchiveInfo()}
                                  </>
                                ) : (
                                  row.message
                                )}
                              </TableCell>
                              <TableCell
                                style={{
                                  width: 300,
                                  paddingLeft: 0,
                                  paddingRight: 0,
                                }}
                              >
                                {["finished"].includes(row.status) == false && (
                                  <button
                                    onClick={() => hadleEditCampaign(row.id)}
                                    title="Editar Campanha"
                                    className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                                  >
                                    <KTSVG
                                      path="/media/icons/duotune/general/gen055.svg"
                                      className="svg-icon-3"
                                    />
                                  </button>
                                )}

                                <button
                                  onClick={() => hadleDuplicateCampaign(row)}
                                  title="Duplicar Campanha"
                                  className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                                >
                                  <KTSVG
                                    path="/media/icons/duotune/general/gen054.svg"
                                    className="svg-icon-3"
                                  />
                                </button>

                                <button
                                  onClick={() => {
                                    goToReport(row.id);
                                  }}
                                  title="Relatório"
                                  className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                                >
                                  <KTSVG
                                    path="/media/icons/duotune/general/gen032.svg"
                                    className="svg-icon-3"
                                  />
                                </button>

                                {row.status != "finished" && (
                                  <button
                                    title="Enviar mensagem de teste"
                                    className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                                    onClick={() => {
                                      setTestCampaignModalOpen(true);
                                      setSelectedCampaign(row);
                                    }}
                                  >
                                    <KTSVG
                                      path="/media/icons/duotune/communication/com012.svg"
                                      className="svg-icon-3"
                                    />
                                  </button>
                                )}

                                {row.status == "started" && (
                                  <button
                                    title="Pausar Campanha"
                                    className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                                    onClick={() =>
                                      handleStatusCampaign(row.id, "paused")
                                    }
                                  >
                                    <KTSVG
                                      path="/media/icons/duotune/general/gen009.svg"
                                      className="svg-icon-3"
                                    />
                                  </button>
                                )}

                                {row.status == "paused" && (
                                  <button
                                    title="Voltar Campanha"
                                    className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                                    onClick={() =>
                                      handleStatusCampaign(row.id, "started")
                                    }
                                  >
                                    <KTSVG
                                      path="/media/icons/duotune/arrows/arr027.svg"
                                      className="svg-icon-3"
                                    />
                                  </button>
                                )}

                                {row.status == "waiting" && (
                                  <button
                                    title="A campanha ainda não chegou a sua data de início."
                                    className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                                  >
                                    <KTSVG
                                      path="/media/icons/duotune/general/gen014.svg"
                                      className="svg-icon-3"
                                    />
                                  </button>
                                )}

                                {row.status == "finished" && (
                                  <>
                                    <button
                                      title="A campanha foi finalizada!"
                                      className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                                    >
                                      <KTSVG
                                        path="/media/icons/duotune/arrows/arr086.svg"
                                        className="svg-icon-3"
                                      />
                                    </button>
                                    <button
                                      title="Reenviar"
                                      className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                                      onClick={() => {
                                        setCampaignModalOpen(true);
                                        setSelectedCampaign(row);
                                        setResendCampaignModalOpen(true);
                                      }}
                                    >
                                      <KTSVG
                                        path="/media/icons/duotune/general/gen016.svg"
                                        className="svg-icon-3"
                                      />
                                    </button>
                                  </>
                                )}

                                <button
                                  title="Excluir"
                                  className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm"
                                  onClick={() => {
                                    setConfirmModalOpen(true);
                                    setSelectedCampaign(row);
                                  }}
                                >
                                  <KTSVG
                                    path="/media/icons/duotune/general/gen027.svg"
                                    className="svg-icon-3"
                                  />
                                </button>
                              </TableCell>
                            </TableRow>
                          );
                        }
                      )
                    )}
                  </TableBody>
                )}
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[20, 50, 100]}
              component="div"
              count={totals}
              rowsPerPage={rowsPerPage}
              page={page}
              labelRowsPerPage={"Resultados por página"}
              labelDisplayedRows={({ from, to, count }) =>
                `${"Total"} ${count} / ${"De"} ${from} ${"à"} ${to}`
              }
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Grid>
          {/* Filtros para monitores */}
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={2}
            xl={2}
            mb={2}
            className={classes.filterColumn}
          >
            {RenderFilters()}
          </Grid>

          {/* Filtros para mobiles */}
          <Drawer
            anchor="left"
            open={open}
            onClose={() => handleDrawer()}
            classes={{
              paper: classes.filterMobile,
            }}
          >
            {RenderFilters()}
          </Drawer>
          {/* Botão de filtros para celular */}
          <Tooltip
            title={
              <Typography gutterBottom color="inherit">
                {i18n.t("ticketManagement.filters.title")}
              </Typography>
            }
          >
            <Fab
              color="primary"
              aria-label="add"
              size="small"
              className={classes.filterMobileButton}
              onClick={() => handleDrawer()}
            >
              <FilterListOutlinedIcon />
            </Fab>
          </Tooltip>
        </Grid>
      </Box>
    </>
  );
};

export default Campaign;
